import { FormHeader } from "app/components/atoms/layout";
import { Select } from "app/components/atoms/select";
import Table from "app/components/atoms/table";
import Typography from "app/components/atoms/typography";
import { DateRangeInputMolecule } from "app/components/molecules/input";
import api from "app/integration/api";
import { useFetcher } from "app/integration/common/hooks";
import MainLayout from "app/layouts/main";
import { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { defaultReportDates } from "app/common/reportDateRange";

const breadcrumbItems = [
  { label: "Laporan Toko", href: "#" },
  { label: "Closing Kasir Harian", href: "#" },
];

function StoreReportDailyClosingPage() {
  const [warehouses, setWarehouses] = useState([]);
  const [queryParams, setQueryParams] = useState({
    warehouse_id: null,
    ...defaultReportDates(),
  });

  useEffect(() => {
    const loadWarehouses = async () => {
      try {
        const response = await api.warehouse.index({ limit: 1000 });
        setWarehouses(response?.data ?? []);
      } catch (err) {
        setWarehouses([]);
      }
    };
    loadWarehouses();
  }, []);

  const warehouseOptions = [
    { value: null, label: "Semua Outlet" },
    ...warehouses.map((warehouse) => ({
      value: warehouse.id,
      label: warehouse.name,
    })),
  ];

  const [rows, , loader] = useFetcher({
    modelName: "storeReport",
    methodName: "dailyClosing",
    payload: { id: queryParams },
  });

  const exportExcel = () => {
    const dataForExcel = (Array.isArray(rows) ? rows : []).map((row) => ({
      Tanggal: row.date,
      Outlet: row.warehouse_name,
      Mulai: row.time_start,
      Selesai: row.time_end,
      "Saldo Awal": row.start_saldo,
      "Saldo Akhir": row.end_saldo,
      "Penjualan Tunai": row.cash_sales,
      "Setoran Kas Kecil": row.petty_cash_deposit,
      "Penarikan Kas Kecil": row.petty_cash_withdrawal,
      Ekspektasi: row.expected_closing,
      Selisih: row.difference,
    }));
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(dataForExcel, {
      header: [
        "Tanggal",
        "Outlet",
        "Mulai",
        "Selesai",
        "Saldo Awal",
        "Saldo Akhir",
        "Penjualan Tunai",
        "Setoran Kas Kecil",
        "Penarikan Kas Kecil",
        "Ekspektasi",
        "Selisih",
      ],
    });
    worksheet["!cols"] = [
      { wch: 14 },
      { wch: 22 },
      { wch: 18 },
      { wch: 18 },
      { wch: 14 },
      { wch: 14 },
      { wch: 16 },
      { wch: 18 },
      { wch: 18 },
      { wch: 14 },
      { wch: 14 },
    ];
    XLSX.utils.book_append_sheet(workbook, worksheet, "Closing Kasir Harian");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const formattedDate = new Date().toISOString().split("T")[0];
    link.download = `laporan_closing_kasir_harian_${formattedDate}.xlsx`;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);
  };

  return (
    <MainLayout
      activeSidebarNavigation="store-report"
      activeSidebarChild="daily-closing"
      breadcrumbItems={breadcrumbItems}
      pageTitle="Laporan Closing Kasir Harian"
      headingButtons={[
        {
          label: "Export Excel",
          type: "button",
          onClick: exportExcel,
        },
      ]}
    >
      <Table.Container>
        <Table.SearchBarWrapper>
          <FormHeader>
            <div className="col-span-2">
              <DateRangeInputMolecule testId="store-report-daily-closing-date-range"
                label="Tanggal"
                from={queryParams.start_date}
                to={queryParams.end_date}
                onFromChange={(e) =>
                  setQueryParams({
                    ...queryParams,
                    start_date: e.target.value,
                  })
                }
                onToChange={(e) =>
                  setQueryParams({ ...queryParams, end_date: e.target.value })
                }
              />
            </div>
            <Select testId="store-report-daily-closing-outlet-select"
              label="Outlet"
              placeholder="Filter Outlet"
              options={warehouseOptions}
              value={queryParams.warehouse_id}
              onChange={(e) =>
                setQueryParams({
                  ...queryParams,
                  warehouse_id: e.target.value,
                })
              }
              absolute={false}
            />
          </FormHeader>
        </Table.SearchBarWrapper>
        <Table.Wrapper asIndex={true}>
          <Table.Header>
            <Table.HeaderRow>
              <Table.Heading>Tanggal</Table.Heading>
              <Table.Heading>Outlet</Table.Heading>
              <Table.Heading>Mulai</Table.Heading>
              <Table.Heading>Selesai</Table.Heading>
              <Table.Heading>
                <p className="text-right">Saldo Awal</p>
              </Table.Heading>
              <Table.Heading>
                <p className="text-right">Saldo Akhir</p>
              </Table.Heading>
              <Table.Heading>
                <p className="text-right">Penjualan Tunai</p>
              </Table.Heading>
              <Table.Heading>
                <p className="text-right">Kas Kecil (+)</p>
              </Table.Heading>
              <Table.Heading>
                <p className="text-right">Kas Kecil (-)</p>
              </Table.Heading>
              <Table.Heading>
                <p className="text-right">Ekspektasi</p>
              </Table.Heading>
              <Table.Heading>
                <p className="text-right">Selisih</p>
              </Table.Heading>
            </Table.HeaderRow>
          </Table.Header>
          <Table.Body>
            {Array.isArray(rows) &&
              rows.map((row, index) => (
                <Table.BodyRow key={index}>
                  <Table.Cell>
                    <Typography.Date date={row.date} format="dd MMM yyyy" />
                  </Table.Cell>
                  <Table.Cell>{row.warehouse_name}</Table.Cell>
                  <Table.Cell>
                    <Typography.Date
                      date={row.time_start}
                      format="dd MMM yyyy HH:mm"
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Typography.Date
                      date={row.time_end}
                      format="dd MMM yyyy HH:mm"
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <p className="text-right">
                      <Typography.Currency number={row.start_saldo} />
                    </p>
                  </Table.Cell>
                  <Table.Cell>
                    <p className="text-right">
                      {row.end_saldo == null ? (
                        "-"
                      ) : (
                        <Typography.Currency number={row.end_saldo} />
                      )}
                    </p>
                  </Table.Cell>
                  <Table.Cell>
                    <p className="text-right">
                      <Typography.Currency number={row.cash_sales} />
                    </p>
                  </Table.Cell>
                  <Table.Cell>
                    <p className="text-right">
                      <Typography.Currency number={row.petty_cash_deposit} />
                    </p>
                  </Table.Cell>
                  <Table.Cell>
                    <p className="text-right">
                      <Typography.Currency
                        number={row.petty_cash_withdrawal}
                      />
                    </p>
                  </Table.Cell>
                  <Table.Cell>
                    <p className="text-right">
                      <Typography.Currency number={row.expected_closing} />
                    </p>
                  </Table.Cell>
                  <Table.Cell fontWeight="semibold">
                    <p className="text-right">
                      {row.difference == null ? (
                        "-"
                      ) : (
                        <Typography.Currency
                          number={row.difference}
                          color={row.difference !== 0 ? "red" : "green"}
                        />
                      )}
                    </p>
                  </Table.Cell>
                </Table.BodyRow>
              ))}
          </Table.Body>
        </Table.Wrapper>
      </Table.Container>
      {loader.state && (
        <p className="text-sm px-2 py-1 text-gray-500 font-light">
          Memuat data...
        </p>
      )}
      {Array.isArray(rows) && rows.length === 0 && !loader.state && (
        <p className="text-sm px-2 py-1 text-gray-500 font-light">
          Tidak ada data untuk filter ini.
        </p>
      )}
    </MainLayout>
  );
}

export default StoreReportDailyClosingPage;
